/* eslint-disable @typescript-eslint/max-params */
import { createSelector } from "@reduxjs/toolkit";
import { getInitSpec } from "../reducers/initReducer.js";
import { getSettingsState } from "../reducers/settingReducer.js";
import { filterBy } from "./dash.js";
import { getAllLayers, getLayers, getLayersTreeConfig, getSelectedLayer, } from "../reducers/layersReducer.js";
import { getSelectedSourceId, getSelectedSourceLayerId, getSelectedSourceLayerLayerId, getSources, getSourcesMetadata, getSourcesSearch, getSourcesTileJSON, } from "../reducers/sourcesReducer.js";
import { getPrimaryVersion, getSecondaryVersion, pastStateSelector, } from "./state.js";
import { normalizeTilesSourceKey, normalizeUrlSourceKey } from "../middlewares/sourcesMiddleware.js";
import { getMarkers, getSelectedMarkerId } from "../reducers/markerReducer.js";
export const getCurrentSpec = createSelector([
    getInitSpec,
    getAllLayers,
    getLayersTreeConfig,
    getSources,
    getSettingsState,
    getMarkers,
], (state, layers = [], layerGroups = [], sources = {}, settings = {}, markers = []) => {
    return {
        ...state,
        ...settings,
        layers,
        sources,
        metadata: {
            mapka: {
                markers,
                layerGroups,
            },
        },
    };
});
export const getPrimaryVersionSpec = createSelector([getPrimaryVersion], (version) => {
    if (version) {
        return getCurrentSpec({
            present: version,
            past: [],
            future: [],
            primaryVersion: null,
            secondaryVersion: null,
        });
    }
    return;
});
export const getSecondaryVersionSpec = createSelector([getSecondaryVersion], (secondary) => {
    if (secondary) {
        return getCurrentSpec({
            present: secondary,
            past: [],
            future: [],
            primaryVersion: null,
            secondaryVersion: null,
        });
    }
    return;
});
export const getPublishedStyle = createSelector(pastStateSelector, (previous) => {
    return previous.map(s => s.init).find(v => v.published);
});
export const getSourcesAndLayers = createSelector([
    getLayers,
    getSources,
    getSourcesTileJSON,
    getSourcesMetadata,
], (allLayers, allSources, sourcesTileJSON, sourcesMetadata) => {
    return Object.entries(allSources).map(([sourceId, source]) => {
        const layers = filterBy(allLayers, "source", sourceId);
        let tileJSON = undefined;
        let metadata = undefined;
        if ("url" in source && typeof source.url === "string") {
            const [key] = normalizeUrlSourceKey(source.url);
            tileJSON = sourcesTileJSON[key];
            metadata = sourcesMetadata[key];
        }
        if ("tiles" in source && typeof source.tiles === "object") {
            const [key] = normalizeTilesSourceKey(source.tiles);
            tileJSON = sourcesTileJSON[key];
            metadata = sourcesMetadata[key];
        }
        if ("data" in source && typeof source.data === "string") {
            const [key] = normalizeUrlSourceKey(source.data);
            metadata = sourcesMetadata[key];
        }
        return {
            sourceId,
            source,
            tileJSON,
            layers,
            metadata,
        };
    });
});
export const getFilteredSourcesAndLayers = createSelector([
    getSourcesAndLayers,
    getSourcesSearch,
], (sources, search) => {
    const searchString = search?.toLocaleLowerCase().trim();
    if (!searchString)
        return sources;
    return sources.filter(({ sourceId }) => sourceId.toLocaleLowerCase().includes(searchString));
});
export const getSelectedSourceInfo = createSelector([
    getSources,
    getSourcesTileJSON,
    getSourcesMetadata,
    getSelectedSourceId,
], (allSources, sourcesTileJSON, sourcesMetadata, sourceId) => {
    if (!sourceId)
        return;
    const source = allSources[sourceId];
    if ("url" in source && source.url) {
        const tileJSON = sourcesTileJSON[source.url];
        const metadata = sourcesMetadata[source.url];
        return {
            sourceId,
            source,
            tileJSON,
            metadata,
        };
    }
    else {
        return {
            sourceId,
            source,
        };
    }
});
export const getHasLayerSelected = createSelector([getSelectedLayer], layer => layer != null);
export const getHasSourceSelected = createSelector([getSelectedSourceId], sourceId => sourceId != null);
export const hasDetailPanelOpen = createSelector([getSelectedLayer, getSelectedSourceId, getSelectedMarkerId], (layer, sourceId, markerId) => layer != null || sourceId != null || markerId != null);
export const getSelectedLayerSourceInfo = createSelector([
    getSelectedLayer,
    getSources,
    getSourcesTileJSON,
    getSourcesMetadata,
    getSelectedSourceLayerId,
    getSelectedSourceLayerLayerId,
], (layer, sources, sourcesTileJSON, sourcesMetadata) => {
    if (!layer)
        return;
    if ("source" in layer) {
        const sourceId = layer.source;
        const source = sources[sourceId];
        if ("url" in source && source.url) {
            const tileJSON = sourcesTileJSON[source.url];
            const metadata = sourcesMetadata[source.url];
            return {
                sourceId,
                source,
                tileJSON,
                metadata,
            };
        }
        else {
            return {
                sourceId,
                source,
            };
        }
    }
    return;
});
